import {
  DEFAULT_NAVIGATION_LAYOUT,
  navigationLayoutList,
} from '../../shared/ui/layout/navigationLayouts';

export const DESIGN_CUSTOMIZATION_STORAGE_KEY = 'bes-ui-design-customization';
export const DESIGN_PREFERENCE_KEYS = [
  'navigationLayout',
  'sidebarCollapsed',
  'density',
  'radius',
  'panelStyle',
  'tableStyle',
  'documentTone',
  'searchStyle',
  'searchPreviewSize',
];

export const defaultDesignCustomization = {
  navigationLayout: 'cleanDashboard',
  sidebarCollapsed: false,
  density: 'comfortable',
  radius: 'soft',
  panelStyle: 'flat',
  tableStyle: 'clean',
  documentTone: 'minimal',
  searchStyle: 'panel',
  searchPreviewSize: 'balanced',
  showFlowBanner: false,
  showSidebarQuickActions: false,
  showSidebarRecentPages: false,
  showTopbarContext: false,
  showHelperText: false,
  showSearchSuggestions: true,
  showSnapshotPanels: false,
  showUsageNotesPanels: false,
  showLedgerNotesPanel: false,
  showCollectionsViewPanel: false,
  showCatalogHealthPanel: false,
  showDataQualityPanel: false,
  showTaxCurrentMixPanel: false,
  showOperatorNotesPanel: false,
  showFormSafetyPanel: false,
  showUnsavedAlerts: true,
  showBrowserUnsavedAlerts: false,
};

export const tenantVisibilityKeys = [
  'showFlowBanner',
  'showSidebarQuickActions',
  'showSnapshotPanels',
  'showUsageNotesPanels',
  'showLedgerNotesPanel',
  'showCollectionsViewPanel',
  'showCatalogHealthPanel',
  'showDataQualityPanel',
  'showTaxCurrentMixPanel',
  'showOperatorNotesPanel',
];

export const userVisibilityKeys = [
  'showSidebarRecentPages',
  'showTopbarContext',
  'showHelperText',
  'showFormSafetyPanel',
  'showUnsavedAlerts',
  'showBrowserUnsavedAlerts',
];

export const allVisibilityKeys = [...tenantVisibilityKeys, ...userVisibilityKeys];

export function pickDesignCustomization(customization = {}) {
  return DESIGN_PREFERENCE_KEYS.reduce((accumulator, key) => {
    accumulator[key] = customization?.[key] ?? defaultDesignCustomization[key];
    return accumulator;
  }, {});
}

function clampBrowserUnsavedAlerts(value) {
  if (!value.showUnsavedAlerts) {
    return {
      ...value,
      showBrowserUnsavedAlerts: false,
    };
  }

  return value;
}

export function pickVisibilityFromCustomization(customization, keys = allVisibilityKeys) {
  return keys.reduce((accumulator, key) => {
    accumulator[key] = Boolean(customization?.[key]);
    return accumulator;
  }, {});
}

export const tableStyleList = [
  {
    value: 'striped',
    label: 'Header band',
    description:
      'Filled brand header with alternating rows, adapted from the Figma header row variant.',
    tags: ['Header', 'Brand bar', 'Desktop'],
  },
  {
    value: 'clean',
    label: 'Divided rows',
    description:
      'Minimal grid with a light header, soft dividers, and a document-style reading rhythm.',
    tags: ['Default', 'Clean', 'Business'],
  },
  {
    value: 'contrast',
    label: 'Selection rows',
    description:
      'Checkbox-ready rows with stronger separators, accent hover, and clearer utility cells.',
    tags: ['Checkbox', 'Selection', 'Action'],
  },
  {
    value: 'mobileCards',
    label: 'Mobile cards',
    description:
      'Card-like rows that collapse cleanly on smaller screens without forcing horizontal scanning.',
    tags: ['Mobile', 'Responsive', 'Cards'],
  },
];

export const designCustomizationOptions = {
  navigationLayout: navigationLayoutList.map((layout) => ({
    value: layout.id,
    label: layout.label,
  })),
  density: [
    { value: 'comfortable', label: 'Comfortable' },
    { value: 'compact', label: 'Compact' },
    { value: 'relaxed', label: 'Relaxed' },
  ],
  radius: [
    { value: 'rounded', label: 'Rounded' },
    { value: 'soft', label: 'Soft' },
    { value: 'sharp', label: 'Sharp' },
  ],
  panelStyle: [
    { value: 'elevated', label: 'Elevated' },
    { value: 'flat', label: 'Flat' },
    { value: 'outlined', label: 'Outlined' },
  ],
  searchStyle: [
    { value: 'panel', label: 'Panel' },
    { value: 'spotlight', label: 'Spotlight' },
    { value: 'minimal', label: 'Minimal' },
  ],
  searchPreviewSize: [
    { value: 'focused', label: 'Focused' },
    { value: 'balanced', label: 'Balanced' },
    { value: 'extended', label: 'Extended' },
  ],
  tableStyle: tableStyleList.map((style) => ({
    value: style.value,
    label: style.label,
  })),
  documentTone: [
    { value: 'studio', label: 'Studio' },
    { value: 'minimal', label: 'Minimal' },
    { value: 'bold', label: 'Bold' },
  ],
};

const densityMap = {
  comfortable: {
    shellGutter: 24,
    sectionGap: 18,
    stackGap: 20,
    panelPadding: 20,
    panelPaddingCompact: 18,
    cardPadding: 18,
    topbarControlHeight: 44,
    topbarMinHeight: 74,
    tablePaddingY: 14,
    documentBandPadding: 24,
    fieldClusterPadding: 18,
  },
  compact: {
    shellGutter: 20,
    sectionGap: 14,
    stackGap: 16,
    panelPadding: 16,
    panelPaddingCompact: 14,
    cardPadding: 14,
    topbarControlHeight: 40,
    topbarMinHeight: 68,
    tablePaddingY: 11,
    documentBandPadding: 20,
    fieldClusterPadding: 14,
  },
  relaxed: {
    shellGutter: 28,
    sectionGap: 22,
    stackGap: 24,
    panelPadding: 24,
    panelPaddingCompact: 20,
    cardPadding: 20,
    topbarControlHeight: 48,
    topbarMinHeight: 80,
    tablePaddingY: 16,
    documentBandPadding: 28,
    fieldClusterPadding: 22,
  },
};

const radiusMap = {
  rounded: {
    sm: 8,
    md: 14,
    lg: 20,
    xl: 28,
    control: 16,
    panel: 20,
    card: 18,
  },
  soft: {
    sm: 6,
    md: 10,
    lg: 14,
    xl: 20,
    control: 12,
    panel: 16,
    card: 14,
  },
  sharp: {
    sm: 4,
    md: 8,
    lg: 12,
    xl: 16,
    control: 10,
    panel: 12,
    card: 12,
  },
};

const panelStyleMap = {
  elevated: {
    shadowSoftLight: '0 12px 30px rgba(15, 23, 42, 0.06)',
    shadowElevatedLight: '0 22px 48px rgba(15, 23, 42, 0.1)',
    shadowSoftDark: '0 18px 42px rgba(0, 0, 0, 0.34)',
    shadowElevatedDark: '0 28px 58px rgba(0, 0, 0, 0.42)',
  },
  flat: {
    shadowSoftLight: '0 8px 18px rgba(15, 23, 42, 0.04)',
    shadowElevatedLight: '0 12px 26px rgba(15, 23, 42, 0.08)',
    shadowSoftDark: '0 10px 22px rgba(0, 0, 0, 0.22)',
    shadowElevatedDark: '0 16px 34px rgba(0, 0, 0, 0.3)',
  },
  outlined: {
    shadowSoftLight: '0 0 0 rgba(0, 0, 0, 0)',
    shadowElevatedLight: '0 0 0 rgba(0, 0, 0, 0)',
    shadowSoftDark: '0 0 0 rgba(0, 0, 0, 0)',
    shadowElevatedDark: '0 0 0 rgba(0, 0, 0, 0)',
  },
};

const navigationLayoutMap = {
  auroraMotion: {
    sidebarWidth: 304,
    mobileDrawerWidth: 320,
    railWidth: 112,
  },
  cleanDashboard: {
    sidebarWidth: 286,
    mobileDrawerWidth: 304,
    railWidth: 112,
  },
  componentSidebar: {
    sidebarWidth: 298,
    mobileDrawerWidth: 314,
    railWidth: 112,
  },
  workspaceDark: {
    sidebarWidth: 288,
    mobileDrawerWidth: 306,
    railWidth: 112,
  },
};

function px(value) {
  return `${value}px`;
}

function getDocumentToneVars(mode, documentTone) {
  if (documentTone === 'minimal') {
    return {
      '--editor-band-main-bg':
        mode === 'dark'
          ? 'linear-gradient(180deg, rgba(24, 31, 38, 0.98), rgba(17, 23, 29, 0.98))'
          : 'linear-gradient(180deg, rgba(255, 255, 255, 0.99), rgba(248, 250, 252, 0.98))',
      '--editor-band-aside-bg':
        mode === 'dark'
          ? 'linear-gradient(180deg, rgba(21, 28, 34, 0.96), rgba(17, 23, 29, 0.96))'
          : 'linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(250, 250, 252, 0.98))',
      '--editor-chip-bg': 'var(--document-neutral-card-bg)',
      '--editor-chip-border': 'var(--document-neutral-card-border)',
      '--editor-chip-text': 'var(--text-main)',
      '--editor-board-head-bg':
        mode === 'dark'
          ? 'linear-gradient(135deg, rgba(18, 24, 30, 0.98), rgba(30, 39, 48, 0.98))'
          : 'linear-gradient(135deg, #273142, #374151)',
    };
  }

  if (documentTone === 'bold') {
    return {
      '--editor-band-main-bg':
        mode === 'dark'
          ? 'radial-gradient(circle at top right, rgba(199, 163, 107, 0.22), transparent 34%), linear-gradient(135deg, rgba(31, 40, 50, 0.98), rgba(17, 23, 29, 0.98))'
          : 'radial-gradient(circle at top right, rgba(124, 58, 237, 0.26), transparent 32%), linear-gradient(135deg, rgba(255, 255, 255, 0.99), rgba(242, 234, 255, 0.98))',
      '--editor-chip-bg':
        mode === 'dark' ? 'rgba(199, 163, 107, 0.14)' : 'rgba(124, 58, 237, 0.14)',
      '--editor-chip-border':
        mode === 'dark' ? 'rgba(199, 163, 107, 0.24)' : 'rgba(124, 58, 237, 0.22)',
      '--editor-chip-text': 'var(--brand-strong)',
      '--editor-board-head-bg':
        mode === 'dark'
          ? 'linear-gradient(135deg, rgba(12, 17, 22, 0.98), rgba(53, 39, 18, 0.98))'
          : 'linear-gradient(135deg, #111827, #6d28d9)',
      '--document-summary-hero-bg':
        mode === 'dark'
          ? 'linear-gradient(135deg, rgba(46, 63, 54, 0.82), rgba(53, 39, 18, 0.76))'
          : 'linear-gradient(135deg, rgba(237, 233, 254, 0.96), rgba(224, 242, 254, 0.92))',
    };
  }

  return {};
}

export function normalizeDesignCustomization(value = {}) {
  const result = { ...defaultDesignCustomization };

  Object.entries(defaultDesignCustomization).forEach(([key, defaultValue]) => {
    const nextValue = value[key];

    if (typeof defaultValue === 'boolean') {
      result[key] = typeof nextValue === 'boolean' ? nextValue : defaultValue;
      return;
    }

    const allowedValues = (designCustomizationOptions[key] || []).map((option) => option.value);
    result[key] = allowedValues.includes(nextValue) ? nextValue : defaultValue;
  });

  return clampBrowserUnsavedAlerts(result);
}

export function getSearchPreviewLimit(size = defaultDesignCustomization.searchPreviewSize) {
  if (size === 'focused') {
    return 4;
  }

  if (size === 'extended') {
    return 8;
  }

  return 6;
}

export function buildDesignCustomization(activePreset, customization) {
  const resolved = normalizeDesignCustomization(customization);
  const density = densityMap[resolved.density] || densityMap.comfortable;
  const radius = radiusMap[resolved.radius] || radiusMap.rounded;
  const panelStyle = panelStyleMap[resolved.panelStyle] || panelStyleMap.elevated;
  const navigationLayout =
    navigationLayoutMap[resolved.navigationLayout] || navigationLayoutMap[DEFAULT_NAVIGATION_LAYOUT];
  const isDark = activePreset.mode === 'dark';

  return {
    cssVars: {
      '--shell-gutter': px(density.shellGutter),
      '--section-gap': px(density.sectionGap),
      '--stack-gap': px(density.stackGap),
      '--panel-padding': px(density.panelPadding),
      '--panel-padding-compact': px(density.panelPaddingCompact),
      '--card-padding': px(density.cardPadding),
      '--topbar-control-height': px(density.topbarControlHeight),
      '--topbar-min-height': px(density.topbarMinHeight),
      '--table-cell-padding-y': px(density.tablePaddingY),
      '--table-header-padding-y': px(density.tablePaddingY),
      '--document-band-padding': px(density.documentBandPadding),
      '--document-field-cluster-padding': px(density.fieldClusterPadding),
      '--radius-sm': px(radius.sm),
      '--radius-md': px(radius.md),
      '--radius-lg': px(radius.lg),
      '--radius-xl': px(radius.xl),
      '--radius-control': px(radius.control),
      '--radius-panel': px(radius.panel),
      '--radius-card': px(radius.card),
      '--nav-sidebar-width': px(navigationLayout.sidebarWidth),
      '--nav-rail-width': px(navigationLayout.railWidth),
      '--nav-mobile-drawer-width': navigationLayout.mobileDrawerWidth
        ? px(navigationLayout.mobileDrawerWidth)
        : px(navigationLayout.sidebarWidth),
      '--shadow-soft': isDark ? panelStyle.shadowSoftDark : panelStyle.shadowSoftLight,
      '--shadow-elevated': isDark ? panelStyle.shadowElevatedDark : panelStyle.shadowElevatedLight,
      ...getDocumentToneVars(activePreset.mode, resolved.documentTone),
    },
    antd: {
      borderRadius: radius.md,
      controlHeight: density.topbarControlHeight,
      menuItemHeight: Math.max(density.topbarControlHeight + 6, 46),
    },
    customization: resolved,
  };
}
